import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  BaseProduct,
  ProductFilters,
  ProductPickerData,
  ProductPickerFilterType,
  ProductPickerProduct,
  ProductPickerSelectedProduct,
  ProductPickerState,
} from "./types";

const initialState: ProductPickerState = {
  products: [],
  selectedProducts: [],
  selectedProductsLoading: false,
  selectingAllProducts: false,
  search: "",
  filters: {
    product_type: [],
    vendor: [],
    tag: [],
    status: [],
    collection_id: [],
  },
};

const slice = createSlice({
  name: "productPicker",
  initialState,
  reducers: {
    openProductPicker: (draft, action: PayloadAction<ProductPickerData>) => {
      draft.data = action.payload;
    },
    closeProductPicker: () => {
      return initialState;
    },
    toggleProduct: (draft, action: PayloadAction<BaseProduct>) => {
      const product = action.payload;
      const index = draft.selectedProducts.findIndex(
        (p) => p.id === product.id
      );
      if (index > -1) {
        draft.selectedProducts.splice(index, 1);
      } else if (draft.data?.type === "gwp") {
        draft.selectedProducts = [product];
      } else {
        draft.selectedProducts.push(product);
      }
    },
    setProductPickerSelectedProducts: (
      draft,
      action: PayloadAction<ProductPickerSelectedProduct[]>
    ) => {
      draft.selectedProducts = action.payload;
    },
    setProductPickerSearch: (draft, action: PayloadAction<string>) => {
      draft.search = action.payload;
    },
    setProductPickerFilter: (
      draft,
      action: PayloadAction<{
        filter: ProductPickerFilterType;
        value: string[];
      }>
    ) => {
      draft.filters[action.payload.filter] = action.payload.value;
    },
    deselectProduct: (draft, action: PayloadAction<string>) => {
      draft.selectedProducts = draft.selectedProducts.filter(
        (p) => p.id !== action.payload
      );
    },
    deselectProductList: (draft, action: PayloadAction<BaseProduct[]>) => {
      const productList = action.payload;
      draft.selectedProducts = draft.selectedProducts.filter(
        (p) => !productList.find((product) => product.id === p.id)
      );
    },
    setProductPickerSelectedProductsLoading: (
      draft,
      action: PayloadAction<boolean>
    ) => {
      draft.selectedProductsLoading = action.payload;
    },
    setProductPickerSelectingAllProducts: (
      draft,
      action: PayloadAction<boolean>
    ) => {
      draft.selectingAllProducts = action.payload;
    },
    setProductPickerProducts: (
      draft,
      action: PayloadAction<ProductPickerProduct[]>
    ) => {
      draft.products = action.payload;
    },
    setProductPickerProductCount: (
      draft,
      action: PayloadAction<number | undefined>
    ) => {
      draft.productCount = action.payload;
    },
  },
  selectors: {
    getProductPickerData: (state) => state.data,
    getProductPickerProducts: (state) => state.products,
    getProductPickerLoadedCount: (state) => state.products.length,
    getProductPickerProductCount: (state) => state.productCount,
    getProductPickerSelectedProducts: (state) => state.selectedProducts,
    getProductPickerSelectedCount: (state) => state.selectedProducts.length,
    getProductPickerSelectedIds: (state) =>
      state.selectedProducts.map((p) => p.id),
    areProductPickerSelectedProductsLoading: (state) =>
      state.selectedProductsLoading,
    isProductPickerSelectingAllProducts: (state) => state.selectingAllProducts,
    getProductPickerSearch: (state) => state.search,
    getProductPickerFilters: (state) => state.filters,
  },
});

export const {
  openProductPicker,
  closeProductPicker,
  setProductPickerSelectedProducts,
  setProductPickerFilter,
  toggleProduct,
  deselectProductList,
  setProductPickerSearch,
  deselectProduct,
  setProductPickerSelectedProductsLoading,
  setProductPickerProducts,
  setProductPickerProductCount,
  setProductPickerSelectingAllProducts,
} = slice.actions;

export const productPickerReducer = slice.reducer;
export const {
  getProductPickerData,
  getProductPickerProducts,
  getProductPickerLoadedCount,
  getProductPickerProductCount,
  getProductPickerSelectedProducts,
  getProductPickerSelectedIds,
  getProductPickerSearch,
  getProductPickerFilters,
  getProductPickerSelectedCount,
  areProductPickerSelectedProductsLoading,
  isProductPickerSelectingAllProducts,
} = slice.selectors;
